/**
 * Run-scoped workbook lifecycle tools for the Excel executor.
 *
 * The Office.js API cannot enumerate or target multiple open workbooks, so
 * every operation in this module is explicitly bridge-only. Successful
 * operations that change workbook focus also update the run pin, ensuring
 * later reads and writes target the workbook identity reported by the host.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import { resetWorkbookScopedRunAccumulators } from "./executorAccumulators";
import { isAbortError } from "../shared/utils/abortError";
import type {
  ExecutorToolRuntimeContext,
  ExecutorWorkbookHandoff,
} from "./executorToolRuntime";
import {
  activateWorksheet,
  addWorkbook,
  getOpenWorkbooks,
  getWorkbookSheets,
  openWorkbook,
  saveWorkbook,
  saveWorkbookAs,
  shouldUseExcelIntentBridge,
} from "./excelHostOps";
import type { OpenWorkbookUpdateLinks } from "./services/factset/spreadsheetIntents";
import type { OpenWorkbookInfo } from "./types";

const WORKBOOK_IDENTITY_POLL_ATTEMPTS = 40;
const WORKBOOK_IDENTITY_POLL_MS = 250;

/**
 * Signals that Excel completed a focus-changing mutation but the conversation
 * could not be relocated to the resulting workbook.
 */
export class ExecutorWorkbookHandoffError extends Error {
  readonly operation: ExecutorWorkbookHandoff["reason"];
  readonly targetWorkbookName: string;

  constructor(
    operation: ExecutorWorkbookHandoff["reason"],
    targetWorkbookName: string,
    cause: unknown,
  ) {
    const detail = cause instanceof Error ? cause.message : String(cause);
    super(`Workbook handoff failed after ${operation}: ${detail}`);
    this.name = "ExecutorWorkbookHandoffError";
    this.operation = operation;
    this.targetWorkbookName = targetWorkbookName;
  }
}

export const LIST_OPEN_WORKBOOKS_TOOL: ChartCapabilityToolDef = {
  name: "list_open_workbooks",
  description:
    "List every workbook currently open in Excel, including exact host names, " +
    "paths, read-only state, and active state. Use before cross-workbook work.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description: "Current plan step id, for traceability.",
      },
    },
    required: ["stepId"],
    additionalProperties: false,
  },
};

export const OPEN_WORKBOOK_TOOL: ChartCapabilityToolDef = {
  name: "open_workbook",
  description:
    "Open a saved workbook from an absolute path, identify the exact workbook " +
    "name assigned by Excel, and make it the run's target workbook.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description: "Current plan step id, for traceability.",
      },
      filePath: {
        type: "string",
        description: "Absolute path to the workbook file.",
      },
      updateLinks: {
        type: ["string", "null"],
        enum: [
          "never",
          "always",
          "prompt",
          "externalOnly",
          "remoteOnly",
          null,
        ],
        description:
          "External-link policy, or null to accept the host default.",
      },
    },
    required: ["stepId", "filePath", "updateLinks"],
    additionalProperties: false,
  },
};

export const CREATE_WORKBOOK_TOOL: ChartCapabilityToolDef = {
  name: "create_workbook",
  description:
    "Create an empty workbook, identify its host-assigned name, and make it " +
    "the run's target workbook. The user must save it through Excel.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description: "Current plan step id, for traceability.",
      },
    },
    required: ["stepId"],
    additionalProperties: false,
  },
};

export const SAVE_WORKBOOK_TOOL: ChartCapabilityToolDef = {
  name: "save_workbook",
  description:
    "Save a named workbook. Pass null to save the workbook pinned to this run.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description: "Current plan step id, for traceability.",
      },
      workbookName: {
        type: ["string", "null"],
        description:
          "Exact workbook name, or null to use the run's pinned workbook.",
      },
    },
    required: ["stepId", "workbookName"],
    additionalProperties: false,
  },
};

export const SAVE_WORKBOOK_AS_TOOL: ChartCapabilityToolDef = {
  name: "save_workbook_as",
  description:
    "Save a named workbook to a new absolute path. Pass null for workbookName " +
    "to use the run pin. The returned host path becomes the new run identity.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description: "Current plan step id, for traceability.",
      },
      filename: {
        type: "string",
        description: "Absolute destination path including the .xlsx filename.",
      },
      workbookName: {
        type: ["string", "null"],
        description:
          "Exact source workbook name, or null to use the run pin.",
      },
    },
    required: ["stepId", "filename", "workbookName"],
    additionalProperties: false,
  },
};

export const ACTIVATE_WORKBOOK_TOOL: ChartCapabilityToolDef = {
  name: "activate_workbook",
  description:
    "Activate a workbook and optionally a specific worksheet, then make that " +
    "workbook the run target. Required before active-workbook-only host actions.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description: "Current plan step id, for traceability.",
      },
      workbookName: {
        type: "string",
        description: "Exact target workbook name reported by Excel.",
      },
      worksheetName: {
        type: ["string", "null"],
        description:
          "Worksheet to activate, or null to choose the first visible worksheet.",
      },
    },
    required: ["stepId", "workbookName", "worksheetName"],
    additionalProperties: false,
  },
};

export const EXECUTOR_WORKBOOK_TOOLS: readonly ChartCapabilityToolDef[] = [
  LIST_OPEN_WORKBOOKS_TOOL,
  OPEN_WORKBOOK_TOOL,
  CREATE_WORKBOOK_TOOL,
  ACTIVATE_WORKBOOK_TOOL,
];

interface OpenWorkbookInput {
  filePath?: string;
  updateLinks?: OpenWorkbookUpdateLinks | null;
}

interface SaveWorkbookInput {
  workbookName?: string | null;
}

interface SaveWorkbookAsInput {
  filename?: string;
  workbookName?: string | null;
}

interface ActivateWorkbookInput {
  workbookName?: string;
  worksheetName?: string | null;
}

/**
 * Execute the open-workbook inventory read.
 *
 * @param _ctx - Run context; unused because this operation does not change pin.
 * @param _rawInput - Provider arguments; no operation-specific fields.
 * @returns A JSON tool result containing normalized workbook metadata.
 */
export async function executeListOpenWorkbooksTool(
  _ctx: ExecutorToolRuntimeContext,
  _rawInput: unknown,
): Promise<string> {
  if (!shouldUseExcelIntentBridge()) {
    return bridgeUnavailableResult("list_open_workbooks");
  }
  try {
    const workbooks = await getOpenWorkbooks();
    return JSON.stringify({
      ok: true,
      operation: "list_open_workbooks",
      workbooks: workbooks.map(toToolWorkbook),
      message: `Found ${workbooks.length} open workbook(s).`,
    });
  } catch (error) {
    return hostFailure("list_open_workbooks", "GetOpenWorkbooks call failed.", error);
  }
}

/**
 * Open, identify, and pin a workbook.
 *
 * @param ctx - Run context whose workbook pin is updated after identification.
 * @param rawInput - Provider arguments containing path and link policy.
 * @returns A JSON tool result describing host success and resolved identity.
 */
export async function executeOpenWorkbookTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const input = (rawInput ?? {}) as OpenWorkbookInput;
  const filePath = optionalString(input.filePath);
  if (!filePath) {
    return invalidInput("open_workbook", "`filePath` is required.");
  }
  if (!shouldUseExcelIntentBridge()) {
    return bridgeUnavailableResult("open_workbook");
  }

  throwIfAborted(signal);
  let before: OpenWorkbookInfo[];
  try {
    before = await getOpenWorkbooks();
  } catch (error) {
    rethrowLifecycleControlError(error, signal);
    return hostFailure(
      "open_workbook",
      "Could not capture workbook inventory before OpenWorkbook.",
      error,
      { filePath },
    );
  }
  const releaseHandoffHold = ctx.beginWorkbookHandoff?.();
  try {
    throwIfAborted(signal);
    const result = await openWorkbook(filePath, {
      ...(input.updateLinks ? { updateLinks: input.updateLinks } : {}),
    });
    if (!result.success) {
      return JSON.stringify({
        ok: false,
        operation: "open_workbook",
        filePath,
        message: "OpenWorkbook did not succeed.",
        ...(result.error ? { error: result.error } : {}),
      });
    }
    const identity = await waitForWorkbookIdentity(
      (after) => identifyOpenedWorkbook(before, after, filePath),
      signal,
    );
    if (!identity?.name) {
      return workbookIdentityFailure("open_workbook", {
        filePath,
      });
    }
    const pinUpdated = await completeWorkbookHandoff(
      ctx,
      identity.name,
      "open_workbook",
    );
    return JSON.stringify({
      ok: true,
      operation: "open_workbook",
      filePath,
      workbookName: identity.name,
      pinUpdated,
      message: `Opened and targeted ${identity.name}.`,
    });
  } catch (error) {
    rethrowLifecycleControlError(error, signal);
    return hostFailure("open_workbook", "OpenWorkbook call failed.", error, {
      filePath,
    });
  } finally {
    releaseHandoffHold?.();
  }
}

/**
 * Create, identify, and pin a blank workbook.
 *
 * @param ctx - Run context whose workbook pin is updated after identification.
 * @param _rawInput - Provider arguments; no operation-specific fields.
 * @returns A JSON tool result containing the host-assigned workbook name.
 */
export async function executeCreateWorkbookTool(
  ctx: ExecutorToolRuntimeContext,
  _rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  if (!shouldUseExcelIntentBridge()) {
    return bridgeUnavailableResult("create_workbook");
  }
  throwIfAborted(signal);
  let before: OpenWorkbookInfo[];
  try {
    before = await getOpenWorkbooks();
  } catch (error) {
    rethrowLifecycleControlError(error, signal);
    return hostFailure(
      "create_workbook",
      "Could not capture workbook inventory before AddWorkbook.",
      error,
    );
  }
  const releaseHandoffHold = ctx.beginWorkbookHandoff?.();
  try {
    throwIfAborted(signal);
    const hostMessage = await addWorkbook();
    const identity = await waitForWorkbookIdentity(
      (after) => identifyCreatedWorkbook(before, after),
      signal,
    );
    if (!identity?.name) {
      return workbookIdentityFailure("create_workbook", {
        hostMessage,
      });
    }
    const pinUpdated = await completeWorkbookHandoff(
      ctx,
      identity.name,
      "create_workbook",
    );
    return JSON.stringify({
      ok: true,
      operation: "create_workbook",
      workbookName: identity.name,
      pinUpdated,
      message: `Created and targeted ${identity.name}.`,
    });
  } catch (error) {
    rethrowLifecycleControlError(error, signal);
    return hostFailure("create_workbook", "AddWorkbook call failed.", error);
  } finally {
    releaseHandoffHold?.();
  }
}

/**
 * Save the explicitly named or run-pinned workbook.
 *
 * @param ctx - Run context supplying the fallback workbook pin.
 * @param rawInput - Provider arguments containing an optional workbook name.
 * @returns A JSON tool result with the authoritative host path when available.
 */
export async function executeSaveWorkbookTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = (rawInput ?? {}) as SaveWorkbookInput;
  const workbookName = resolveWorkbook(ctx, input.workbookName);
  if (!workbookName) {
    return invalidInput(
      "save_workbook",
      "`workbookName` is required when the run has no pinned workbook.",
    );
  }
  if (!shouldUseExcelIntentBridge()) {
    return bridgeUnavailableResult("save_workbook");
  }
  try {
    const result = await saveWorkbook(workbookName);
    return JSON.stringify({
      ok: result.success,
      operation: "save_workbook",
      workbookName: workbookName ?? null,
      fullName: result.fullName ?? null,
      message: result.success
        ? "Workbook saved."
        : "SaveWorkbook did not succeed.",
      ...(result.error ? { error: result.error } : {}),
    });
  } catch (error) {
    return hostFailure("save_workbook", "SaveWorkbook call failed.", error, {
      workbookName: workbookName ?? null,
    });
  }
}

/**
 * Save a workbook to a new path and update run ownership to its new identity.
 *
 * @param ctx - Run context supplying and receiving workbook identity.
 * @param rawInput - Provider arguments containing destination and source.
 * @returns A JSON tool result with the authoritative host path and new pin.
 */
export async function executeSaveWorkbookAsTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const input = (rawInput ?? {}) as SaveWorkbookAsInput;
  const filename = optionalString(input.filename);
  if (!filename) {
    return invalidInput("save_workbook_as", "`filename` is required.");
  }
  const sourceWorkbookName = resolveWorkbook(ctx, input.workbookName);
  if (!sourceWorkbookName) {
    return invalidInput(
      "save_workbook_as",
      "`workbookName` is required when the run has no pinned workbook.",
    );
  }
  if (!shouldUseExcelIntentBridge()) {
    return bridgeUnavailableResult("save_workbook_as");
  }
  const pinnedWorkbookName = optionalString(ctx.session.pinnedWorkbookName);
  const preservesWorkbookEvidence =
    pinnedWorkbookName !== undefined &&
    normalizePath(pinnedWorkbookName) === normalizePath(sourceWorkbookName);
  const releaseHandoffHold = ctx.beginWorkbookHandoff?.();
  try {
    throwIfAborted(signal);
    const result = await saveWorkbookAs(filename, sourceWorkbookName);
    let nextName: string | undefined;
    let confirmedFullName = result.fullName;
    if (result.success && result.fullName) {
      nextName = basename(result.fullName);
    } else if (result.success) {
      const identity = await waitForWorkbookIdentity(
        (workbooks) => identifyWorkbookByRequestedIdentity(workbooks, filename),
        signal,
      );
      if (!identity?.name) {
        return workbookIdentityFailure("save_workbook_as", {
          sourceWorkbookName,
          filename,
          hostMutationSucceeded: true,
        });
      }
      nextName = identity.name;
      confirmedFullName = optionalString(identity.fullName);
    }
    const pinUpdated = nextName
      ? await completeWorkbookHandoff(ctx, nextName, "save_workbook_as", {
          preserveWorkbookEvidence: preservesWorkbookEvidence,
        })
      : false;
    return JSON.stringify({
      ok: result.success,
      operation: "save_workbook_as",
      sourceWorkbookName: sourceWorkbookName ?? null,
      workbookName: nextName ?? sourceWorkbookName ?? null,
      filename,
      fullName: confirmedFullName ?? null,
      pinUpdated,
      message: result.success
        ? "Workbook saved to new path."
        : "SaveWorkbookAs did not succeed.",
      ...(result.error ? { error: result.error } : {}),
    });
  } catch (error) {
    rethrowLifecycleControlError(error, signal);
    return hostFailure("save_workbook_as", "SaveWorkbookAs call failed.", error, {
      sourceWorkbookName: sourceWorkbookName ?? null,
      filename,
    });
  } finally {
    releaseHandoffHold?.();
  }
}

/**
 * Activate a workbook through one of its visible worksheets and update the pin.
 *
 * @param ctx - Run context whose target workbook is updated after activation.
 * @param rawInput - Provider arguments containing workbook and optional sheet.
 * @returns A JSON tool result containing the activated workbook and worksheet.
 */
export async function executeActivateWorkbookTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const input = (rawInput ?? {}) as ActivateWorkbookInput;
  const workbookName = optionalString(input.workbookName);
  if (!workbookName) {
    return invalidInput("activate_workbook", "`workbookName` is required.");
  }
  if (!shouldUseExcelIntentBridge()) {
    return bridgeUnavailableResult("activate_workbook");
  }

  let worksheetName = optionalString(input.worksheetName);
  if (!worksheetName) {
    try {
      throwIfAborted(signal);
      const rawSheets = await getWorkbookSheets(workbookName, {
        properties: ["visibility"],
      });
      throwIfAborted(signal);
      worksheetName = firstActivatableWorksheet(rawSheets);
    } catch (error) {
      rethrowLifecycleControlError(error, signal);
      return hostFailure(
        "activate_workbook",
        "Could not list sheets in target workbook.",
        error,
        { workbookName },
      );
    }
  }
  if (!worksheetName) {
    return JSON.stringify({
      ok: false,
      operation: "activate_workbook",
      workbookName,
      message: `Workbook "${workbookName}" has no visible worksheets to activate.`,
      error: "no_worksheets",
    });
  }

  try {
    throwIfAborted(signal);
    await activateWorksheet(worksheetName, { workbookName });
    const pinUpdated = await completeWorkbookHandoff(
      ctx,
      workbookName,
      "activate_workbook",
      { activeSheetName: worksheetName },
    );
    return JSON.stringify({
      ok: true,
      operation: "activate_workbook",
      workbookName,
      worksheetName,
      pinUpdated,
      message: `Activated ${workbookName} → ${worksheetName}.`,
    });
  } catch (error) {
    rethrowLifecycleControlError(error, signal);
    return hostFailure(
      "activate_workbook",
      "ActivateWorksheet call failed.",
      error,
      { workbookName, worksheetName },
    );
  }
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function resolveWorkbook(
  ctx: ExecutorToolRuntimeContext,
  value: unknown,
): string | undefined {
  return optionalString(value) ?? optionalString(ctx.session.pinnedWorkbookName);
}

async function completeWorkbookHandoff(
  ctx: ExecutorToolRuntimeContext,
  workbookName: string,
  reason: ExecutorWorkbookHandoff["reason"],
  options: {
    activeSheetName?: string;
    preserveWorkbookEvidence?: boolean;
  } = {},
): Promise<boolean> {
  const sourceWorkbookName = optionalString(ctx.session.pinnedWorkbookName);
  const workbookChanged =
    !sourceWorkbookName ||
    normalizePath(sourceWorkbookName) !== normalizePath(workbookName);
  ctx.session.pinnedWorkbookName = workbookName;
  if (options.activeSheetName) {
    ctx.session.sessionLockSheet = options.activeSheetName;
  }
  if (workbookChanged && options.preserveWorkbookEvidence !== true) {
    resetWorkbookScopedRunAccumulators(ctx.accumulators);
  }
  if (workbookChanged || reason === "save_workbook_as") {
    try {
      await ctx.onWorkbookHandoff?.({
        ...(sourceWorkbookName ? { sourceWorkbookName } : {}),
        targetWorkbookName: workbookName,
        ...(options.activeSheetName
          ? { activeSheetName: options.activeSheetName }
          : {}),
        reason,
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new ExecutorWorkbookHandoffError(reason, workbookName, error);
    }
  }
  return true;
}

function bridgeUnavailableResult(operation: string): string {
  return JSON.stringify({
    ok: false,
    operation,
    message:
      "Office.js does not expose a multi-workbook API. Workbook lifecycle " +
      "tools require the FactSet Spreadsheet intent bridge.",
    fallbackMode: "office-js",
    error: "not_supported",
  });
}

function invalidInput(operation: string, message: string): string {
  return JSON.stringify({
    ok: false,
    operation,
    message,
    error: "invalid_input",
  });
}

function hostFailure(
  operation: string,
  message: string,
  error: unknown,
  details: Record<string, unknown> = {},
): string {
  return JSON.stringify({
    ok: false,
    operation,
    ...details,
    message,
    error: error instanceof Error ? error.message : String(error),
  });
}

function toToolWorkbook(entry: OpenWorkbookInfo): {
  name: string;
  path?: string;
  readOnly: boolean;
  isActive: boolean;
} {
  const path = optionalString(entry.fullName) ?? optionalString(entry.path);
  return {
    name: optionalString(entry.name) ?? "",
    ...(path ? { path } : {}),
    readOnly: entry.readOnly === true,
    isActive: entry.isActive === true,
  };
}

async function listOpenWorkbooksBestEffort(): Promise<OpenWorkbookInfo[]> {
  try {
    return await getOpenWorkbooks();
  } catch {
    return [];
  }
}

/**
 * Poll the host inventory until a lifecycle mutation exposes its new identity.
 *
 * @param identify - Resolves the expected workbook from one inventory snapshot.
 * @returns The identified workbook, or `undefined` after the bounded timeout.
 */
async function waitForWorkbookIdentity(
  identify: (workbooks: readonly OpenWorkbookInfo[]) => OpenWorkbookInfo | undefined,
  signal?: AbortSignal,
): Promise<OpenWorkbookInfo | undefined> {
  for (let attempt = 0; attempt < WORKBOOK_IDENTITY_POLL_ATTEMPTS; attempt += 1) {
    throwIfAborted(signal);
    const workbooks = await listOpenWorkbooksBestEffort();
    throwIfAborted(signal);
    const identity = identify(workbooks);
    if (identity) return identity;
    if (attempt + 1 < WORKBOOK_IDENTITY_POLL_ATTEMPTS) {
      await sleep(WORKBOOK_IDENTITY_POLL_MS, signal);
    }
  }
  return undefined;
}

function sleep(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    const onAbort = (): void => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

function rethrowLifecycleControlError(
  error: unknown,
  signal?: AbortSignal,
): void {
  if (isAbortError(error)) throw error;
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
  if (error instanceof ExecutorWorkbookHandoffError) throw error;
}

function workbookIdentityFailure(
  operation: string,
  details: Record<string, unknown>,
): string {
  return JSON.stringify({
    ok: false,
    operation,
    ...details,
    message:
      "The host completed the workbook mutation, but the resulting workbook identity could not be resolved.",
    error: "workbook_identity_unresolved",
  });
}

function identifyOpenedWorkbook(
  before: readonly OpenWorkbookInfo[],
  after: readonly OpenWorkbookInfo[],
  filePath: string,
): OpenWorkbookInfo | undefined {
  const normalizedPath = normalizePath(filePath);
  const requestedName = basename(filePath).toLocaleLowerCase();
  const exactPathMatches = after.filter(
    (entry) =>
      normalizePath(reportedWorkbookPath(entry) ?? "") === normalizedPath &&
      optionalString(entry.name) !== undefined,
  );
  if (exactPathMatches.length === 1) return exactPathMatches[0];
  if (exactPathMatches.length > 1) return undefined;

  const beforeKeys = new Set(before.map(workbookIdentityKey));
  const newNameOnlyMatches = after.filter(
    (entry) =>
      (entry.name ?? "").toLocaleLowerCase() === requestedName &&
      optionalString(entry.name) !== undefined &&
      reportedWorkbookPath(entry) === undefined &&
      !beforeKeys.has(workbookIdentityKey(entry)),
  );
  return newNameOnlyMatches.length === 1 ? newNameOnlyMatches[0] : undefined;
}

function identifyWorkbookByRequestedIdentity(
  workbooks: readonly OpenWorkbookInfo[],
  requestedPath: string,
): OpenWorkbookInfo | undefined {
  const normalizedPath = normalizePath(requestedPath);
  const exactPathMatches = workbooks.filter(
    (entry) =>
      normalizePath(reportedWorkbookPath(entry) ?? "") === normalizedPath &&
      optionalString(entry.name) !== undefined,
  );
  return exactPathMatches.length === 1 ? exactPathMatches[0] : undefined;
}

function identifyCreatedWorkbook(
  before: readonly OpenWorkbookInfo[],
  after: readonly OpenWorkbookInfo[],
): OpenWorkbookInfo | undefined {
  const added = after.filter(
    (entry) =>
      optionalString(entry.name) !== undefined &&
      !before.some((baselineEntry) =>
        observationsIdentifySameWorkbook(baselineEntry, entry),
      ),
  );
  return added.length === 1 ? added[0] : undefined;
}

/**
 * Determine whether two host inventory records can safely represent the same
 * workbook while metadata is progressively hydrated between polling samples.
 *
 * A stable path is preferred when both observations expose one. Matching names
 * are also sufficient because a name-only baseline cannot distinguish paths;
 * treating such a record as existing fails closed if Excel permits duplicates.
 *
 * @param baselineEntry - Workbook observation captured before host mutation.
 * @param candidateEntry - Workbook observation returned by a later poll.
 * @returns `true` when the candidate must not be classified as newly created.
 */
function observationsIdentifySameWorkbook(
  baselineEntry: OpenWorkbookInfo,
  candidateEntry: OpenWorkbookInfo,
): boolean {
  const baselinePath = reportedWorkbookPath(baselineEntry);
  const candidatePath = reportedWorkbookPath(candidateEntry);
  if (
    baselinePath &&
    candidatePath &&
    normalizePath(baselinePath) === normalizePath(candidatePath)
  ) {
    return true;
  }

  const baselineName = optionalString(baselineEntry.name);
  const candidateName = optionalString(candidateEntry.name);
  return (
    baselineName !== undefined &&
    candidateName !== undefined &&
    baselineName.toLocaleLowerCase() === candidateName.toLocaleLowerCase()
  );
}

function workbookIdentityKey(entry: OpenWorkbookInfo): string {
  return normalizePath(reportedWorkbookPath(entry) ?? entry.name ?? "");
}

function reportedWorkbookPath(entry: OpenWorkbookInfo): string | undefined {
  const fullName = optionalString(entry.fullName);
  if (fullName) return fullName;
  const path = optionalString(entry.path);
  const name = optionalString(entry.name);
  if (!path || !name) return path;
  return basename(path).toLocaleLowerCase() === name.toLocaleLowerCase()
    ? path
    : `${path.replace(/[\\/]$/, "")}\\${name}`;
}

function normalizePath(value: string): string {
  return value.replaceAll("\\", "/").toLocaleLowerCase();
}

function basename(value: string): string {
  const pieces = value.replaceAll("\\", "/").split("/");
  return pieces.at(-1) ?? value;
}

function firstActivatableWorksheet(raw: unknown): string | undefined {
  const sheets =
    Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object" && Array.isArray((raw as { sheets?: unknown }).sheets)
        ? (raw as { sheets: unknown[] }).sheets
        : [];
  for (const item of sheets) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const name = optionalString(record.name);
    if (!name || name.toLocaleUpperCase() === "__FDSCACHE__") continue;
    if (
      typeof record.sheetType === "string" &&
      record.sheetType !== "xlWorksheet"
    ) {
      continue;
    }
    if (
      typeof record.visibility === "string" &&
      record.visibility !== "xlSheetVisible"
    ) {
      continue;
    }
    return name;
  }
  return undefined;
}
